/** Local video helpers — CI clones `@brushfire/utils` from `dev-10` without these exports. */

export const VIDEO_UPLOAD_MAX_BYTES = 200 * 1024 * 1024

export const VIDEO_MISSING_SOURCE_MSG = 'Add a video link or upload a video to continue'

export const VIDEO_UNSUPPORTED_LINK_MSG =
  'Unsupported video source. Please use a YouTube, Vimeo, or direct MP4 link.'

export const VIDEO_FILE_TOO_LARGE_MSG = 'File exceeds the 200 MB upload limit'

export const VIDEO_UNSUPPORTED_FILE_MSG =
  'Unsupported file format. Please upload an MP4 (H.264) video.'

export const VIDEO_HEADING_REQUIRED_MSG = 'Question heading is required'

export type ParsedVideoPlayback =
  | { kind: 'youtube'; videoId: string; embedUrl: string }
  | { kind: 'vimeo'; videoId: string; embedUrl: string }
  | { kind: 'mp4'; src: string }

const YOUTUBE_ID = /^[a-zA-Z0-9_-]{11}$/
const VIMEO_ID = /^\d{6,12}$/
const MP4_PATH = /\.mp4(?:$|[?#])/i

export function isUploadedVideoServePath(raw: string): boolean {
  const t = raw.trim()
  if (!t || t.startsWith('data:')) return false
  return (
    t.includes('/draft/question-videos/') ||
    /\/api\/v1\/sp\/instruments\/[^/]+\/question-videos\//i.test(t)
  )
}

function youtubeEmbedUrl(videoId: string): string {
  return `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}?rel=0`
}

function vimeoEmbedUrl(videoId: string): string {
  return `https://player.vimeo.com/video/${encodeURIComponent(videoId)}`
}

function youtubeIdFromUrl(parsed: URL): string | null {
  const host = parsed.hostname.replace(/^www\./i, '').toLowerCase()
  if (host === 'youtu.be') {
    const id = parsed.pathname.split('/').find((segment) => segment.length > 0) ?? ''
    return YOUTUBE_ID.test(id) ? id : null
  }
  if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'youtube-nocookie.com') {
    const v = parsed.searchParams.get('v')
    if (v && YOUTUBE_ID.test(v)) return v
    const parts = parsed.pathname.split('/').filter(Boolean)
    if (
      (parts[0] === 'embed' || parts[0] === 'shorts' || parts[0] === 'live') &&
      parts[1] &&
      YOUTUBE_ID.test(parts[1])
    ) {
      return parts[1]
    }
  }
  return null
}

function vimeoIdFromUrl(parsed: URL): string | null {
  const host = parsed.hostname.replace(/^www\./i, '').toLowerCase()
  if (host === 'vimeo.com' || host === 'player.vimeo.com') {
    const parts = parsed.pathname.split('/').filter(Boolean)
    const id = host === 'player.vimeo.com' && parts[0] === 'video' ? parts[1] : parts[0]
    return id && VIMEO_ID.test(id) ? id : null
  }
  return null
}

function tryParseHttpUrl(raw: string): URL | null {
  try {
    const parsed = new URL(raw.trim())
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null
    return parsed
  } catch {
    return null
  }
}

export function parseVideoLink(raw: string): ParsedVideoPlayback | null {
  const t = raw.trim()
  if (!t) return null
  if (isUploadedVideoServePath(t)) {
    return { kind: 'mp4', src: t }
  }
  const parsed = tryParseHttpUrl(t)
  if (!parsed) return null
  const yt = youtubeIdFromUrl(parsed)
  if (yt) {
    return { kind: 'youtube', videoId: yt, embedUrl: youtubeEmbedUrl(yt) }
  }
  const vimeo = vimeoIdFromUrl(parsed)
  if (vimeo) {
    return { kind: 'vimeo', videoId: vimeo, embedUrl: vimeoEmbedUrl(vimeo) }
  }
  if (MP4_PATH.test(parsed.pathname) || MP4_PATH.test(parsed.href)) {
    return { kind: 'mp4', src: parsed.href }
  }
  return null
}

export function isSupportedVideoLink(raw: string): boolean {
  return parseVideoLink(raw) !== null
}

export function validateVideoUploadFile(file: {
  type?: string
  size: number
  name?: string
}): string | null {
  const name = (file.name ?? '').toLowerCase()
  const type = (file.type ?? '').toLowerCase()
  const looksMp4 = type === 'video/mp4' || name.endsWith('.mp4')
  if (!looksMp4) return VIDEO_UNSUPPORTED_FILE_MSG
  if (file.size > VIDEO_UPLOAD_MAX_BYTES) return VIDEO_FILE_TOO_LARGE_MSG
  return null
}
